import { notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object } from 'yup';
import {
  RuleDescriptionField,
  RuleNameField,
  ruleDescriptionSchema,
  ruleNameSchema,
  schemaResolver,
} from '../../../TransactionRuleForm';
import {
  BackButton,
  CancelButton,
  FormLayout,
  Section,
} from '../../../TransactionRuleModal';
import { useCreateTransactionRuleContext } from '../../CreateTransactionRuleContext';
import { CreateButton } from './CreateButton';

export const TransactionGeneralSettingsStep = () => {
  const {
    transactionRule,
    transactionModifications,
    cancelCreation,
    resetWizard,
    submitCreation,
    prevStep,
  } = useCreateTransactionRuleContext();
  const [isSubmitting, setIsSubmitting] = useBoolean();

  const schema = useMemo(() => {
    return object().shape({
      name: ruleNameSchema(transactionModifications),
      description: ruleDescriptionSchema,
    });
  }, [transactionModifications]);

  const methods = useForm({
    defaultValues: {
      name: transactionRule?.name ?? '',
      description: transactionRule?.description,
    },
    resolver: schemaResolver(schema),
  });
  const { handleSubmit, formState, watch } = methods;
  const name = watch('name');
  const description = watch('description');

  const submitForm = async (shouldRefresh) => {
    const updatedTransactionRule = {
      ...transactionRule,
      name,
      description,
    };
    setIsSubmitting.setTrue();
    try {
      await submitCreation(updatedTransactionRule, shouldRefresh);
      if (!shouldRefresh) {
        resetWizard();
      }
    } catch (error) {
      console.error(error);
      notify.error('Failed to create new data rule');
    }
    setIsSubmitting.setFalse();
  };

  return (
    <FormProvider {...methods}>
      <FormLayout onSubmit={handleSubmit(submitForm)}>
        <FormLayout.Content>
          <Section
            title="General settings"
            iconType="generalSettings"
          >
            <RuleNameField autoFocus />
            <RuleDescriptionField rows={19} />
          </Section>
        </FormLayout.Content>
        <FormLayout.Footer>
          <BackButton
            onClick={prevStep}
            disabled={isSubmitting}
          />
          <div className="flex-1" />
          <CancelButton
            onClick={cancelCreation}
            disabled={isSubmitting}
          />
          <CreateButton
            onClick={(shouldRefresh) => {
              handleSubmit(() => submitForm(shouldRefresh))();
            }}
            disabled={!formState.isValid || isSubmitting}
          />
        </FormLayout.Footer>
      </FormLayout>
    </FormProvider>
  );
};
